import { faro, LogLevel } from '@grafana/faro-react';
import { format } from 'date-fns';
import { all, call, put, takeEvery, delay, select } from 'redux-saga/effects';
import { createAsyncAction } from 'typesafe-actions';
import { Events, EnvelopeStatus, ErrorCodes, SIGNATURE_FONTS } from '../common/constants';
import { eventEmitter } from '../index';
import saveFile from '../lib/FileSaver';
import pdfCache from '../lib/pdfCache';
import { calculateFontSize } from '../lib/useAutomaticFontSizing';
import type { IResponse, IEnvelopeSubmitResult } from '../resources/Server';
import type { IDocument, IBlock, DateFormatType, IEnvelope, ISigner } from '../store/types';
import {
  ActionTypes,
  termsAccepted,
  startRequest,
  endRequest,
  confirmSignatureModalDone,
  signField,
  fetchThumbnailsByEnvelope,
  fetchThumbnailsByEnvelopeDone,
  downloadedEnvelope,
  updateSignerSigningSettings,
} from './actions';
import type { IRootState } from './index';

const RETRY_LIMIT = 5;
const DELAY = 1000;
const DEFAULT_FONT_SIZE = 12;
const DEFAULT_FONT_FAMILY = 'Courier';
const DEFAULT_SIGNATURE_FONT = 'Damion';
const DEFAULT_TIME_FORMAT = ', hh:mm:ss A';

const fetchEnvelope = createAsyncAction(ActionTypes.FETCH_ENVELOPE, ActionTypes.FETCH_ENVELOPE_DONE, ActionTypes.ERROR)<
  string,
  IEnvelope,
  string
>();

const submitEnvelope = createAsyncAction(
  ActionTypes.SUBMIT_ENVELOPE,
  ActionTypes.SUBMIT_ENVELOPE_DONE,
  ActionTypes.ERROR
)<string, IEnvelopeSubmitResult, string>();

const submitEsignDisclosure = createAsyncAction(
  ActionTypes.SUBMIT_ESIGN_DISCLOSURE,
  ActionTypes.SUBMIT_ESIGN_DISCLOSURE_DONE,
  ActionTypes.ERROR
)<string, IResponse<null>, string>();

const downloadEnvelopePdf = createAsyncAction(
  ActionTypes.DOWNLOAD_ENVELOPE,
  ActionTypes.DOWNLOAD_ENVELOPE_DONE,
  ActionTypes.ERROR
)<null, string, string>();

export function* handleFetchEnvelope(server: any, action: ReturnType<typeof fetchEnvelope.request>) {
  for (let i = 0; i < RETRY_LIMIT; i++) {
    try {
      yield put(startRequest());
      const res: IResponse<IEnvelope> = yield call([server, server.getEnvelopeById], action.payload);
      const { envelopeId, status, documents, signer } = res.result;
      const isMeSigner = signer.title === 'Me';
      if (isMeSigner && signer.signingSettings?.signatureFont) {
        signer.signingSettings.signatureType = 'text';
        signer.signingSettings.saveSigningSettings = true;
        signer.signingSettings.signatureValue = signer.signingSettings?.signatureText;
        signer.signingSettings.initialsValue = signer.signingSettings?.initialsText;
        delete signer.signingSettings.signatureText;
        delete signer.signingSettings.initialsText;
      } else if (
        isMeSigner &&
        signer.signingSettings?.signatureImageBase64 &&
        signer.signingSettings?.initialsImageBase64
      ) {
        signer.signingSettings.signatureType = 'image';
        signer.signingSettings.saveSigningSettings = true;
        signer.signingSettings.signatureValue = signer.signingSettings?.signatureImageBase64;
        signer.signingSettings.initialsValue = signer.signingSettings?.initialsImageBase64;
        delete signer.signingSettings.signatureImageBase64;
        delete signer.signingSettings.initialsImageBase64;
      }
      let documentPresignedURLS: string[];

      const docList = signer.executedDocuments.length ? signer.executedDocuments : documents;
      documentPresignedURLS = yield all(
        docList.map((doc) => call([server, server.getPdfPreSignedUrl], { envelopeId, documentId: doc.documentId }))
      );
      documents.forEach((doc, i) => {
        pdfCache[doc.documentId] = documentPresignedURLS[i];
      });

      yield put(endRequest());
      eventEmitter.emit(Events.ENVELOPE_RETRIEVED, { envelopeId, status });
      if (res.result.status === EnvelopeStatus.COMPLETE) {
        eventEmitter.emit(Events.ENVELOPE_SUBMITTED, res.result);
      }
      // Remove for DOP release
      // yield put(fetchThumbnailsByEnvelope(envelopeId, documents[0].numberOfPages));
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      return yield put(fetchEnvelope.success(res.result));
    } catch (error) {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      const { response } = error;
      if (response === undefined) {
        eventEmitter.emit(Events.ERROR, 'An error occurred during the request!');
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        return yield put(fetchEnvelope.failure(error.message));
        // @ts-expect-error -- This code was written prior to enforcing lint rules
      } else if (i === RETRY_LIMIT - 1 || error.response.data.statusCode === 401) {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        eventEmitter.emit(Events.ERROR, error.response.data);
        yield put(endRequest());
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        return yield put(fetchEnvelope.failure('Error Fetching Envelope'));
        // @ts-expect-error -- This code was written prior to enforcing lint rules
      } else if (ErrorCodes[error.response.data.errorCode]) {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        const event = ErrorCodes[error.response.data.errorCode];
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        eventEmitter.emit(event, error.response.data);
        yield put(endRequest());
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        return yield put(fetchEnvelope.failure(`Envelope error: ${event}`));
      }
      yield delay(DELAY);
    }
  }
}

export function* handleSubmitEnvelope(server: any, action: ReturnType<typeof submitEnvelope.request>) {
  try {
    yield put(startRequest());
    verifySize(action.payload);

    // Get phone number from state if available
    const state: IRootState = yield select();
    if (state.modal.signerPhoneNumber) {
      // If we have collected a phone number, include it in the envelope data
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      if (!action.payload.data.signerMetadata) {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        action.payload.data.signerMetadata = {};
      }
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      action.payload.data.signerMetadata.phoneNumber = state.modal.signerPhoneNumber.phoneNumber;
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      action.payload.data.signerMetadata.email = state.modal.signerPhoneNumber.email;
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      action.payload.data.signerMetadata.signerId = state.modal.signerPhoneNumber.signerId;
    }

    const res: IResponse<IEnvelopeSubmitResult> = yield call([server, server.submitEnvelope], action.payload);
    const { executedDocuments, envelopeId } = res.result;
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const documentPresignedURLS = yield all(
      executedDocuments.map((doc) =>
        call([server, server.getPdfPreSignedUrl], { envelopeId, documentId: doc.documentId })
      )
    );

    executedDocuments.forEach((doc, i) => {
      pdfCache[doc.documentId] = documentPresignedURLS[i];
    });

    eventEmitter.emit(Events.ENVELOPE_SUBMITTED, res.result);
    yield put(submitEnvelope.success(res.result));
  } catch (error) {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const response = error.response;
    if (response === undefined) {
      eventEmitter.emit(Events.ERROR, 'An error occurred during the request!');
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      return yield put(submitEnvelope.failure(error.message));
    }
    eventEmitter.emit(Events.ERROR, response.data);
    yield put(submitEnvelope.failure('Error Submitting Envelope'));
    yield put(endRequest());
  }
}

export function* handleEsignDisclosureAgreement(server: any, action: any) {
  try {
    yield put(startRequest());
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const res = yield call([server, server.submitEsignDisclosureAgreement], action.payload);
    yield put(submitEsignDisclosure.success(res.result));
    yield put(termsAccepted());
  } catch (error) {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const { response } = error;
    if (response === undefined) {
      eventEmitter.emit(Events.ERROR, 'An error occurred during the request!');
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      return yield put(submitEsignDisclosure.failure(error.message));
    }
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    eventEmitter.emit(Events.ERROR, error.response.data);
    yield put(submitEsignDisclosure.failure('Error Submitting ESIGN Disclosure Agreement'));
  } finally {
    yield put(endRequest());
  }
}

export function* handleSignatureModal(server: any, action: any) {
  const { envelopeId, signerId, pageNumber, documentId, blockId, signatureSettings, useExistingSettings } =
    action.payload;
  try {
    yield put(startRequest());
    yield call([server, server.submitSignatureAgreement], envelopeId, signerId, signatureSettings, useExistingSettings);
    const selectedBlock = [pageNumber, blockId, documentId].every((id) => id !== undefined);
    if (selectedBlock) {
      yield put(signField(documentId, pageNumber, blockId));
    }
    yield put(confirmSignatureModalDone());
  } catch (error) {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const { response } = error;
    if (response === undefined) {
      eventEmitter.emit(Events.ERROR, 'An error occurred during the request!');
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      return yield put(error(error.message));
    }
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    eventEmitter.emit(Events.ERROR, error.response.data);
    yield put(submitEsignDisclosure.failure('Error Submitting Signature Agreement'));
  } finally {
    if (!useExistingSettings) {
      yield put(updateSignerSigningSettings(signatureSettings));
    }
    yield put(endRequest());
  }
}

export function* handleGetThumbnailsByEnvelope(server: any, action: any) {
  const { envelopeId } = action.payload;
  let err;
  let response: any = [];
  while (response.length === 0 && !err) {
    try {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      const res = yield call([server, server.getThumbnailsByEnvelope], { envelopeId });
      if (res.errors.length) {
        err = res.errors[0];
      }
      response = res.result;
      yield put(endRequest());
      if (response.length && response[0].thumbnails.length === action.payload.numberOfPages) {
        yield put(fetchThumbnailsByEnvelopeDone(response));
      } else {
        yield delay(5000);
      }
    } catch (error) {
      err = error;
      yield put(endRequest());
    }
  }
  if (response.length) {
    if (response[0].thumbnails.length !== action.payload.numberOfPages) {
      yield put(fetchThumbnailsByEnvelope(envelopeId, action.payload.numberOfPages));
    }
  }
}

export function* handleEnvelopeDownload(server: any, action: any) {
  const { envelopeId, signerId, fileName, alreadySigned, fileType } = action.payload;
  try {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const res = yield call([server, server.downloadEnvelope], {
      envelopeId,
      signerId,
      fileType,
    });
    const objUrl = URL.createObjectURL(new Blob([res.data]));
    yield call(saveFile, objUrl, fileName, res.data);
    yield put(downloadedEnvelope(objUrl, fileName, alreadySigned, fileType));
  } catch {
    eventEmitter.emit(Events.ERROR, `Error: handleEnvelopeDownload - ${fileType}`);
    yield put(downloadEnvelopePdf.failure(`Error: handleEnvelopeDownload - ${fileType}`));
  }
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default function* rootSaga(server: any) {
  yield all([
    takeEvery(ActionTypes.FETCH_ENVELOPE, handleFetchEnvelope, server),
    takeEvery(ActionTypes.SUBMIT_ENVELOPE, handleSubmitEnvelope, server),
    takeEvery(ActionTypes.SUBMIT_ESIGN_DISCLOSURE, handleEsignDisclosureAgreement, server),
    takeEvery(ActionTypes.CONFIRM_SIGNATURE_MODAL, handleSignatureModal, server),
    takeEvery(ActionTypes.FETCH_THUMBNAILS_BY_ENVELOPE, handleGetThumbnailsByEnvelope, server),
    takeEvery(ActionTypes.DOWNLOAD_ENVELOPE, handleEnvelopeDownload, server),
  ]);
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const getBlockValueAndFamilyFont = (
  block: IBlock,
  signatureFont: string | undefined,
  signer: ISigner,
  dateFormat: DateFormatType
): { familyFont: string; value: any } => {
  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const getSignatureBlocksInfo = () => ({
    value: block.value,
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    familyFont: SIGNATURE_FONTS[signatureFont || DEFAULT_SIGNATURE_FONT].fontFamily,
  });

  const blockTypeHandlers = {
    Signature: getSignatureBlocksInfo(),
    Initials: getSignatureBlocksInfo(),
    DateSigned: {
      value: format(block.value! as string, dateFormat),
      familyFont: DEFAULT_FONT_FAMILY,
    },
    TimeSigned: {
      value: format(block.value! as string, dateFormat + DEFAULT_TIME_FORMAT),
      familyFont: DEFAULT_FONT_FAMILY,
    },
    FullName: {
      value: [signer.firstName, signer.middleName, signer.lastName].filter(Boolean).join(' '),
      familyFont: DEFAULT_FONT_FAMILY,
    },
    default: {
      value: block.value,
      familyFont: DEFAULT_FONT_FAMILY,
    },
  };

  // @ts-expect-error -- This code was written prior to enforcing lint rules
  return blockTypeHandlers[block.blockType] || blockTypeHandlers.default;
};

function verifySize(payload: any) {
  const blockTypesToCheckFontSize = ['Signature', 'Initials', 'DateSigned', 'TimeSigned', 'FullName'];
  const otherBlocks = ['Checkbox', 'Text'];
  let isErrorLogged = false;
  payload?.data?.documents?.forEach((document: IDocument) =>
    document.blocks?.forEach((block: IBlock) => {
      if (blockTypesToCheckFontSize.includes(block.blockType) && (!block.fontSize || block.fontSize < 1)) {
        if (!isErrorLogged) {
          logInvalidFontSizeError(payload);
          isErrorLogged = true;
        }

        // @ts-expect-error -- This code was written prior to enforcing lint rules
        const { value, familyFont } = getBlockValueAndFamilyFont(block, payload.data.signatureFont, payload.signer);
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        block.fontSize = calculateFontSize(value, DEFAULT_FONT_SIZE, block.height, block.width, familyFont, false);

        if (!block.fontSize || block.fontSize < 1) {
          const fontSize =
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            DEFAULT_FONT_SIZE >= block.height
              ? block.blockType === 'TimeSigned'
                ? // @ts-expect-error -- This code was written prior to enforcing lint rules
                  block.height / 2 + 1
                : // @ts-expect-error -- This code was written prior to enforcing lint rules
                  block.height - 1
              : DEFAULT_FONT_SIZE;
          block.fontSize = fontSize;
        }
      }

      if (!isErrorLogged && otherBlocks.includes(block.blockType) && (!block.fontSize || block.fontSize < 1)) {
        logInvalidFontSizeError(payload);
        isErrorLogged = true;
      }
    })
  );
}

function logInvalidFontSizeError(payload: { envelopeId: string; data: any }) {
  faro.api?.pushLog(['Payload for stamp envelope contains at least a block with invalid fontSize value'], {
    level: LogLevel.WARN,
    context: {
      envelopeId: payload.envelopeId,
      payloadForStamp: JSON.stringify(payload.data),
    },
  });
}
